import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Avatar,
  IconButton,
  Container,
  MenuItem,
  Menu,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import LogoIcon from "../../../../assets/images/logo-icon.png";
import TypingIndicator from "../../../../Shared-ui/SharedComponents/TypingIndicator";
import { useDispatch } from "react-redux";
import { StartLoading, StopLoading } from "../../../../Shared-ui/Store/LoadingReducer/LoaderActions";
import { ShowError } from "../../../../Shared-ui/Store/MessageReducer/MsgActions";
import PlayGroundInitialMessage from "../../../Components/PlayGroundIntialMessage";
import { Message, UserMessage } from "../../../../Shared-ui/Types/PlayGround";
import { v4 as uuidv4 } from "uuid";
import { PlayGroundEnum } from "../../../../Shared-ui/Enum/PlayGroundEnum";
import { GetSession } from "../../../../Shared-ui/utils/Auth/AuthHelpers";
import { marked } from "marked";
import AgentPlayGroundTools from "../../../Components/AgentPlayGroundTools";
import AgentQuestions from "../../../Components/AgentQuestions";
import striptags from "striptags";
import BaseInstance from "../../../../Services/AxiosInstance/BaseInstance";
import { AGENT } from "../../../../Services/Endpoint";
import { Agent } from "../../../../Shared-ui/Types/Agent";
import VoiceRecorder from "../../../../Shared-ui/SharedComponents/VoiceRecorder";
import { audioBlobToBase64 } from "../../../../utils/audioUtils";

interface PlaygroundChatProps {
  agentData: Agent | null;
  isEditMode: boolean;
}

const PlaygroundChat: React.FC<PlaygroundChatProps> = ({ agentData, isEditMode }) => {
  const dispatch = useDispatch();
  const [messages, setMessages] = useState<Message[]>([]);
  const [userInput, setUserInput] = useState('');
  const [botTyping, setBotTyping] = useState(false);
  const [conversationId, setConversationId] = useState('');
  const [showQuestions, setShowQuestions] = useState(true);
  const [showInitialMessage, setShowInitialMessage] = useState(true);
  const [initialMessage, setInitialMessage] = useState('');
  const chatEndRef = useRef<HTMLDivElement>(null);
  const isCancelledStream = useRef(false);


  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  // Load initial message
  useEffect(() => {
    if (agentData && showInitialMessage) {
      setInitialMessage(agentData.description || `Hi! I'm ${agentData.name}. How can I help you today?`);
    }
  }, [agentData, showInitialMessage]);

  const sendMessageQuestions = (question: string) => {
    setUserInput(question);
    setShowQuestions(false);
    setShowInitialMessage(false);
    sendChatMessage(question);
  };

  const sendChatMessage = async (inputText?: string) => {
    const messageText = inputText || userInput.trim();
    if (!messageText || !agentData) return;

    // Hide initial message and questions after first interaction
    if (showInitialMessage) {
      setShowInitialMessage(false);
    }
    if (showQuestions) {
      setShowQuestions(false);
    }

    // Add user message
    const userMessage: Message = {
      sender: PlayGroundEnum.USER,
      userData: {
        input: messageText,
        conversation_id: conversationId,
      },
    };

    setMessages(prev => [...prev, userMessage]);
    setUserInput('');
    setBotTyping(true);

    // Add bot typing message
    const botMessage: Message = {
      sender: PlayGroundEnum.AGENT,
      agentData: {
        response: '',
        conversation_id: conversationId,
        metadata: {
          tool_calls: [],
        },
        citations: [],
      },
    };

    setMessages(prev => [...prev, botMessage]);

    try {
      const session = GetSession();
      // Determine if streaming should be enabled based on agent metadata
      const shouldStream = agentData?.agent_metadata?.streaming ?? true;
      const response = await fetch(`${AGENT}/${agentData.id}/execute?stream=${shouldStream}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${session}`,
          "Content-Type": "application/json",
          Accept: shouldStream ? "text/event-stream" : "application/json",
        },
        body: JSON.stringify({
          input: messageText,
          conversation_id: conversationId || "",
        }),
        mode: "cors",
        credentials: "same-origin",
      });

      if (!response.ok) {
        throw new Error("Failed to get response");
      }

      if (shouldStream) {
        // Handle streaming response
        if (!response.body) {
          throw new Error("Failed to stream");
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder("utf-8");

        let buffer = "";
        let content = "";
        let tool_calls: any[] = [];
        let references: any[] = [];

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          if (isCancelledStream.current) break;

          buffer += decoder.decode(value, { stream: true });

          const lines = buffer.split("\n");
          for (const line of lines) {
            if (line.startsWith("data: ")) {
              const jsonPart = line.replace(/^data: /, "").trim();

              if (jsonPart === "[DONE]") return;

              try {
                const parsed = JSON.parse(jsonPart);
                if (!parsed.is_complete) {
                  content += parsed.response;
                  if (parsed.conversation_id) {
                    setConversationId(parsed.conversation_id);
                  }
                  tool_calls = parsed.metadata?.tool_calls || [];
                  const sanitizedContent = await marked.parse(content);
                  
                  // Update bot message with streaming content
                  setMessages(prev => prev.map((msg, index) => 
                    index === prev.length - 1 // Last message is the bot message
                      ? {
                          ...msg,
                          agentData: {
                            response: sanitizedContent,
                            conversation_id: parsed.conversation_id || conversationId,
                            metadata: {
                              tool_calls: tool_calls,
                              extra_data: { references: references },
                            },
                            citations: parsed.citations || [],
                          }
                        }
                      : msg
                  ));
                }
              } catch (parseError) {
                console.error("Failed to parse streaming response:", parseError);
              }
            }
          }

          // Keep partial buffer (in case a line is split across chunks)
          buffer = lines[lines.length - 1];
        }
      } else {
        // Handle non-streaming response
        const data = await response.json();
        
        if (data.conversation_id) {
          setConversationId(data.conversation_id);
        }
        
        const sanitizedContent = await marked.parse(data.response || "");
        
        // Update bot message with complete response
        setMessages(prev => prev.map((msg, index) => 
          index === prev.length - 1 // Last message is the bot message
            ? {
                ...msg,
                agentData: {
                  response: sanitizedContent,
                  conversation_id: data.conversation_id || conversationId,
                  metadata: {
                    tool_calls: data.metadata?.tool_calls || [],
                    extra_data: { references: data.metadata?.extra_data?.references || [] },
                  },
                  citations: data.citations || [],
                }
              }
            : msg
        ));
      }

    } catch (error: any) {
      console.error('Chat error:', error);
      dispatch(ShowError(`Failed to send message: ${error?.response?.data?.message || error.message}`));
      
      // Update bot message with error
      setMessages(prev => prev.map((msg, index) => 
        index === prev.length - 1 // Last message is the bot message
          ? {
              ...msg,
              agentData: {
                response: 'Sorry, I encountered an error. Please try again.',
                conversation_id: conversationId,
                metadata: {
                  tool_calls: [],
                },
                citations: [],
              }
            }
          : msg
      ));
    } finally {
      setBotTyping(false);
    }
  };

  const clearChat = () => {
    setMessages([]);
    setShowInitialMessage(true);
    setShowQuestions(true);
    setConversationId('');
  };

  const sendVoiceMessage = async (audioBlob: Blob) => {
    if (!agentData) return;

    // Hide initial message and questions after first interaction
    if (showInitialMessage) {
      setShowInitialMessage(false);
    }
    if (showQuestions) {
      setShowQuestions(false);
    }

    // Add user message with "Processing voice..." placeholder
    const userMessage: Message = {
      sender: PlayGroundEnum.USER,
      userData: {
        input: "🎤 Processing voice...",
        conversation_id: conversationId,
      },
    };

    setMessages(prev => [...prev, userMessage]);
    setBotTyping(true);

    // Add bot typing message
    const botMessage: Message = {
      sender: PlayGroundEnum.AGENT,
      agentData: {
        response: '',
        conversation_id: conversationId,
        metadata: {
          tool_calls: [],
        },
        citations: [],
      },
    };

    setMessages(prev => [...prev, botMessage]);
    
    try {
      // Convert audio blob to base64 using chunked approach to avoid stack overflow
      const base64Audio = await audioBlobToBase64(audioBlob);
      
      const requestPayload = {
        audio_data: base64Audio,
        conversation_id: conversationId || "",
      };

      console.log('🎤 Sending voice message with payload:', requestPayload);

      const response = await fetch(
        `${AGENT}/${agentData.id}/voice`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${GetSession()}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestPayload),
          mode: "cors",
          credentials: "same-origin",
        }
      );

      if (!response.ok) {
        throw new Error("Failed to process voice message");
      }

      const result = await response.json();
      
      // Parse the response content
      const parsedResponse = await marked.parse(result.response);
      
      // Update user message with transcribed text
      setMessages(prev => prev.map((msg, index) => 
        index === prev.length - 2 // Second to last message is the user message
          ? {
              ...msg,
              userData: {
                input: result.metadata?.transcribed_text || "Voice message",
                conversation_id: result.conversation_id || conversationId,
              }
            }
          : msg
      ));
      
      // Update agent message with response
      setMessages(prev => prev.map((msg, index) => 
        index === prev.length - 1 // Last message is the agent message
          ? {
              ...msg,
              agentData: {
                response: parsedResponse,
                conversation_id: result.conversation_id || conversationId,
                metadata: result.metadata,
                audioResponse: result.audio_response,
              }
            }
          : msg
      ));
      
      setConversationId(result.conversation_id || conversationId);
      
    } catch (error: any) {
      console.error('Voice message error:', error);
      dispatch(ShowError(`Failed to send voice message: ${error?.response?.data?.message || error.message}`));
      
      // Update user message with error
      setMessages(prev => prev.map((msg, index) => 
        index === prev.length - 2 // Second to last message is the user message
          ? {
              ...msg,
              userData: {
                input: "❌ Voice message failed",
                conversation_id: conversationId,
              }
            }
          : msg
      ));
      
      // Update bot message with error
      setMessages(prev => prev.map((msg, index) => 
        index === prev.length - 1 // Last message is the agent message
          ? {
              ...msg,
              agentData: {
                response: 'Sorry, I encountered an error processing your voice message. Please try again.',
                conversation_id: conversationId,
                metadata: {
                  tool_calls: [],
                },
                citations: [],
              }
            }
          : msg
      ));
    } finally {
      setBotTyping(false);
    }
  };

  const renderMessage = (message: Message, index: number) => {
    if (message.sender === PlayGroundEnum.USER) {
      return (
        <Box key={index} sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Box
            sx={{
              backgroundColor: '#1976d2',
              color: 'white',
              p: 1.5,
              borderRadius: '18px 18px 4px 18px',
              maxWidth: '80%',
              wordWrap: 'break-word',
            }}
          >
            <Typography variant="body2" sx={{ fontSize: '13px', lineHeight: 1.4 }}>
              {message.userData?.input}
            </Typography>
          </Box>
        </Box>
      );
    } else {
      return (
        <Box key={index} sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
          <Box sx={{ maxWidth: '80%', width: '100%' }}>
            {/* Tool calls */}
            {message.agentData?.metadata?.tool_calls && message.agentData.metadata.tool_calls.length > 0 && (
              <AgentPlayGroundTools tools={message.agentData.metadata.tool_calls} />
            )}
            
            {/* Agent response */}
            <Box
              sx={{
                backgroundColor: 'white',
                color: 'black',
                p: 1.5,
                borderRadius: '18px 18px 18px 4px',
                border: '1px solid #e0e0e0',
                wordWrap: 'break-word',
              }}
            >
              {message.agentData?.response ? (
                <Typography
                  variant="body2"
                  sx={{ fontSize: '13px', lineHeight: 1.4 }}
                  dangerouslySetInnerHTML={{
                    __html: message.agentData.response,
                  }}
                />
              ) : (
                <TypingIndicator />
              )}
            </Box>

            {/* Audio Response */}
            {message.agentData?.audioResponse && (
              <Box sx={{ mt: 1, mb: 1 }}>
                <VoiceRecorder
                  onRecordingComplete={() => {}}
                  disabled={true}
                  size="small"
                  showPlayback={true}
                  showMicIcon={false}
                  audioUrl={(() => {
                    try {
                      // Use audio format from metadata, fallback to mp3
                      const audioFormat = message?.agentData?.metadata?.audio_format || 'mp3';
                      const audioBlob = new Blob([Uint8Array.from(atob(message.agentData.audioResponse), c => c.charCodeAt(0))], { type: `audio/${audioFormat}` });
                      return URL.createObjectURL(audioBlob);
                    } catch (error) {
                      console.error('Error creating audio URL:', error);
                      return undefined;
                    }
                  })()}
                />
              </Box>
            )}
          </Box>
        </Box>
      );
    }
  };

  if (!isEditMode || !agentData) {
    return null;
  }

  return (
    <Box
      sx={{
        width: 380,
        backgroundColor: 'white',
        borderLeft: '1px solid #e0e0e0',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      {/* Chat Header */}
      <Box
        sx={{
          backgroundColor: '#1976d2',
          color: 'white',
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          minHeight: 60,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar src={LogoIcon} sx={{ width: 32, height: 32 }} />
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', fontSize: '14px' }}>
              {agentData.name ? `${agentData.name} Playground` : 'Agent Playground'}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.8, fontSize: '11px' }}>
              Online
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={clearChat}
          sx={{ color: 'white' }}
          size="small"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
          </svg>
        </IconButton>
      </Box>

      {/* Chat Messages Area */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          p: 2,
          backgroundColor: '#f8f9fa',
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        {/* Initial Message */}
        {showInitialMessage && (
          <PlayGroundInitialMessage 
            message={initialMessage} 
            incomingTextColor="#1976d2" 
          />
        )}

        {/* Chat Messages */}
        {messages.map((message, index) => renderMessage(message, index))}
        
        <div ref={chatEndRef} />
      </Box>

      {/* Input Field */}
      <Box
        sx={{
          display: "flex",
          gap: 1,
          alignItems: "flex-start",
          p: 2,
          bgcolor: "rgba(255, 255, 255, 0.2)",
          width: "100%",
          position: "sticky",
          bottom: 0,
          borderTop: "1px solid rgba(24, 24, 27, .1)",
          flexDirection: "column",
        }}
      >
        {/* Suggested Questions */}
        {showQuestions && agentData && (
          <AgentQuestions
            sendMessageQuestions={sendMessageQuestions}
            agentId={agentData.id}
          />
        )}

        <Box
          sx={{
            border: "1px solid rgba(24, 24, 27, .1)",
            boxShadow: "0px 0px 15px 0px rgba(0, 0, 0, 0.1)",
            borderRadius: "20px",
            width: "100%",
            position: "relative",
            padding: "12px 16px",
            background: "#fff",
          }}
        >
          <TextField
            fullWidth
            placeholder="Type a message..."
            value={userInput}
            onChange={(e) => setUserInput(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                sendChatMessage();
              }
            }}
            disabled={botTyping}
            variant="standard"
            multiline
            maxRows={4}
            InputProps={{
              disableUnderline: true,
              sx: {
                fontSize: '14px',
                '& textarea': {
                  resize: 'none',
                },
              },
            }}
            sx={{
              '& .MuiInputBase-root': {
                fontSize: '14px',
              },
            }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: "8px",
              right: "8px",
              display: "flex",
              gap: 1,
              alignItems: "center",
            }}
          >
            {agentData?.agent_metadata?.voice_enabled && (
              <VoiceRecorder
                onRecordingComplete={sendVoiceMessage}
                disabled={botTyping}
                size="small"
              />
            )}
            <IconButton
              onClick={() => sendChatMessage()}
              disabled={!userInput.trim() || botTyping}
              sx={{
                backgroundColor: '#1976d2',
                color: 'white',
                width: 32,
                height: 32,
                '&:hover': {
                  backgroundColor: '#1565c0',
                },
                '&:disabled': {
                  backgroundColor: '#ccc',
                },
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="22" y1="2" x2="11" y2="13"></line>
                <polygon points="22,2 15,22 11,13 2,9 22,2"></polygon>
              </svg>
            </IconButton>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default PlaygroundChat;
